import type { Branch } from "../../api/types";
import css from "./BranchTabs.module.css";

/**
 * Branch filter chip-row (B.2.1). Active state = purple border per the
 * A2 two-tier rule (filters use purple, not the role colour).
 *
 * Includes a synthetic "all" chip at the start (opt-out via `allChip` —
 * конфигуратор EO правит один филиал, «ВСЕ» там бессмысленно).
 */
export interface BranchTabsProps {
  branches: Branch[];
  active: string;
  onChange: (branchId: string) => void;
  /** Синтетический чип «ВСЕ» в начале ряда (по умолчанию есть). */
  allChip?: boolean;
  /**
   * Акцент активного чипа. Фильтры — "purple" (правило A2, дефолт);
   * конфигуратор EO — "role": там чип — контекст-переключатель «какой
   * филиал правим», не фильтр, и эталон screen-16 (.bp.on, HTML:67)
   * осознанно красит его цветом роли.
   */
  accent?: "purple" | "role";
  /** 28.09 («Юниверситет»): false — имя филиала как в конфигураторе («Псих»), без КАПСА. */
  upper?: boolean;
  /** 29.09 («Юниверситет»): свои чипы после филиалов (например «Grades») — тот же вид и выбор. */
  extra?: { id: string; label: string }[];
}

export function BranchTabs({ branches, active, onChange, allChip = true, accent = "purple", upper = true, extra = [] }: BranchTabsProps) {
  const activeCls = accent === "role" ? css.activeRole : css.active;
  const items = [
    ...(allChip ? [{ id: "all", label: "ВСЕ" }] : []),
    ...branches.map((b) => ({ id: b.id, label: upper ? branchLabel(b) : (b.name?.trim() || b.id) })),
    ...extra,
  ];

  return (
    <nav className={css.row} aria-label="Фильтр по филиалу">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          className={`${css.chip} ${active === it.id ? activeCls : ""}`}
          onClick={() => onChange(it.id)}
          aria-pressed={active === it.id}
        >
          {it.label}
        </button>
      ))}
    </nav>
  );
}

/** Short branch label, fall back to first 4 chars of full name. */
function branchLabel(b: Branch): string {
  const name = b.name?.trim() ?? b.id;
  if (name.length <= 4) return name.toUpperCase();
  /* "Амонг" → "Амнг", "Психо" → "Псих" — already done by tenant in db */
  return name.toUpperCase();
}
